import {
  Eye,
  FileImage,
  FileText,
  Image as ImageIcon,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useCurrentFirm } from '@/components/firms/hooks/useCurrentFirm';
import { TemplatesV2Page } from '@/components/templates-v2/TemplatesV2Page';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useSetFirmFooterMutation,
  useSetFirmHeaderFooterDocxMutation,
  useSetFirmLetterheadMutation,
  useSetFirmLogoMutation,
  useSetFirmWatermarkMutation,
} from '@/services/api/firmsService';
import { captureError } from '@/services/monitoring/sentry';
import { Firm } from '@/services/types/firms-types';

import { FirmHipaaStaticFileManagerContent } from '../hipaa/FirmHipaaStaticFileManager';
import {
  useUpdateFirmHipaaAttorneyRelationshipForm,
  useUpdateFirmHipaaReproductiveRightsAttestation,
} from '../hipaa/hooks';
import { FirmTemplateResolutionPanel } from './FirmTemplateResolutionPanel';

/** Describes a single firm design collateral slot */
interface CollateralSlot {
  key: string;
  label: string;
  description: string;
  /** Best-available URL field (cleaned if available, else raw) */
  urlField: keyof Pick<
    Firm,
    'letterhead_url' | 'firm_footer_url' | 'firm_logo_url' | 'firm_watermark_url'
  >;
  /** Raw (original upload) URL field */
  rawUrlField: keyof Pick<
    Firm,
    | 'raw_letterhead_url'
    | 'raw_firm_footer_url'
    | 'raw_firm_logo_url'
    | 'raw_firm_watermark_url'
  >;
  /** Cleaned (background-removed) URL field */
  cleanedUrlField: keyof Pick<
    Firm,
    | 'cleaned_letterhead_url'
    | 'cleaned_firm_footer_url'
    | 'cleaned_firm_logo_url'
    | 'cleaned_firm_watermark_url'
  >;
}

const COLLATERAL_SLOTS: CollateralSlot[] = [
  {
    key: 'letterhead',
    label: 'Letterhead',
    description: 'Top-of-page header image',
    urlField: 'letterhead_url',
    rawUrlField: 'raw_letterhead_url',
    cleanedUrlField: 'cleaned_letterhead_url',
  },
  {
    key: 'footer',
    label: 'Footer',
    description: 'Bottom-of-page footer image',
    urlField: 'firm_footer_url',
    rawUrlField: 'raw_firm_footer_url',
    cleanedUrlField: 'cleaned_firm_footer_url',
  },
  {
    key: 'logo',
    label: 'Logo',
    description: 'Firm branding image',
    urlField: 'firm_logo_url',
    rawUrlField: 'raw_firm_logo_url',
    cleanedUrlField: 'cleaned_firm_logo_url',
  },
  {
    key: 'watermark',
    label: 'Watermark',
    description: 'Document watermark image',
    urlField: 'firm_watermark_url',
    rawUrlField: 'raw_firm_watermark_url',
    cleanedUrlField: 'cleaned_firm_watermark_url',
  },
];

/** Preview dialog for viewing raw vs cleaned collateral images */
const CollateralPreviewDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  rawUrl: string | null;
  cleanedUrl: string | null;
}> = ({ open, onOpenChange, label, rawUrl, cleanedUrl }) => {
  const [viewMode, setViewMode] = useState<'raw' | 'cleaned'>('cleaned');

  const activeUrl = viewMode === 'cleaned' ? (cleanedUrl ?? rawUrl) : rawUrl;
  const hasBothVersions = !!rawUrl && !!cleanedUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Toggle between raw and cleaned */}
          {hasBothVersions && (
            <div className="flex items-center gap-1 rounded border border-zinc-200 bg-zinc-50 p-1 text-xs">
              <button
                type="button"
                className={`flex items-center gap-1 rounded px-2 py-1 font-medium transition-colors ${
                  viewMode === 'cleaned'
                    ? 'border border-zinc-200 bg-white text-zinc-900'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
                onClick={() => setViewMode('cleaned')}
              >
                <Sparkles className="h-3 w-3" />
                Cleaned
              </button>
              <button
                type="button"
                className={`flex items-center gap-1 rounded px-2 py-1 font-medium transition-colors ${
                  viewMode === 'raw'
                    ? 'border border-zinc-200 bg-white text-zinc-900'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
                onClick={() => setViewMode('raw')}
              >
                <FileImage className="h-3 w-3" />
                Original
              </button>
            </div>
          )}

          {/* Image preview */}
          <div className="flex items-center justify-center rounded border border-zinc-200 bg-zinc-50 p-3">
            {activeUrl ? (
              <img
                src={activeUrl}
                alt={`${label} - ${viewMode}`}
                className="max-h-96 max-w-full object-contain"
              />
            ) : (
              <span className="py-8 text-sm text-zinc-500">
                No image available
              </span>
            )}
          </div>

          {/* Status label */}
          <p className="text-xs text-zinc-500">
            {viewMode === 'cleaned' && cleanedUrl
              ? 'Background removed'
              : cleanedUrl
                ? 'Original upload'
                : 'Background removal pending'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Small inline component for a single collateral row */
const CollateralRow: React.FC<{
  slot: CollateralSlot;
  rawUrl: string | null;
  cleanedUrl: string | null;
  isLoading: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
}> = ({ slot, rawUrl, cleanedUrl, isLoading, onUpload, onClear }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const displayUrl = cleanedUrl ?? rawUrl;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-2.5">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleChange}
          disabled={isLoading}
        />

        {/* Thumbnail / placeholder */}
        <div className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded border border-zinc-200 bg-zinc-50">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={slot.label}
              className="h-full w-full object-contain"
            />
          ) : (
            <ImageIcon className="h-4 w-4 text-zinc-300" />
          )}
        </div>

        {/* Label + status */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
            {slot.label}
          </p>
          <p className="truncate text-xs text-zinc-500">
            {slot.description}
            {rawUrl && !cleanedUrl && ' \u00B7 Cleaning...'}
            {cleanedUrl && ' \u00B7 Cleaned'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1.5">
          {displayUrl && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setPreviewOpen(true)}
              disabled={isLoading}
            >
              <Eye className="mr-1 h-3 w-3" />
              View
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => inputRef.current?.click()}
            disabled={isLoading}
          >
            <Upload className="mr-1 h-3 w-3" />
            {displayUrl ? 'Replace' : 'Upload'}
          </Button>
          {displayUrl && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
              onClick={onClear}
              disabled={isLoading}
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Clear
            </Button>
          )}
        </div>
      </div>

      <CollateralPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        label={slot.label}
        rawUrl={rawUrl}
        cleanedUrl={cleanedUrl}
      />
    </>
  );
};

/** Row for the header/footer DOCX upload (not an image — accepts .docx only) */
const HeaderFooterDocxRow: React.FC<{
  url: string | null;
  isLoading: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
}> = ({ url, isLoading, onUpload, onClear }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = '';
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <input
        ref={inputRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={handleChange}
        disabled={isLoading}
      />

      {/* Icon placeholder */}
      <div className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded border border-zinc-200 bg-zinc-50">
        <FileText className={`h-4 w-4 ${url ? 'text-blue-500' : 'text-zinc-300'}`} />
      </div>

      {/* Label + status */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
          Header / Footer DOCX
        </p>
        <p className="truncate text-xs text-zinc-500">
          Word document with pre-configured header and footer for generic templates
          {url && ' \u00B7 Uploaded'}
        </p>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1.5">
        {url && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => window.open(url, '_blank')}
            disabled={isLoading}
          >
            <Eye className="mr-1 h-3 w-3" />
            View
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => inputRef.current?.click()}
          disabled={isLoading}
        >
          <Upload className="mr-1 h-3 w-3" />
          {url ? 'Replace' : 'Upload'}
        </Button>
        {url && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
            onClick={onClear}
            disabled={isLoading}
          >
            <Trash2 className="mr-1 h-3 w-3" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export const FirmTemplatesPage: React.FC = () => {
  const { firm } = useCurrentFirm();
  const firmId = firm?.id || '';

  // HIPAA hooks
  const { handleUpdate: updateAttyForm } =
    useUpdateFirmHipaaAttorneyRelationshipForm({ firmId });
  const { handleUpdate: updateReproForm } =
    useUpdateFirmHipaaReproductiveRightsAttestation({ firmId });

  // Collateral mutations
  const [setLetterhead, { isLoading: isSettingLetterhead }] =
    useSetFirmLetterheadMutation();
  const [setFooter, { isLoading: isSettingFooter }] =
    useSetFirmFooterMutation();
  const [setLogo, { isLoading: isSettingLogo }] = useSetFirmLogoMutation();
  const [setWatermark, { isLoading: isSettingWatermark }] =
    useSetFirmWatermarkMutation();
  const [setHeaderFooterDocx, { isLoading: isSettingHeaderFooterDocx }] =
    useSetFirmHeaderFooterDocxMutation();

  const isAnyLoading =
    isSettingLetterhead || isSettingFooter || isSettingLogo || isSettingWatermark || isSettingHeaderFooterDocx;

  /** Upload file directly to the right endpoint (no cropping) */
  const uploadCollateral = useCallback(
    async (slotKey: string, file: File | null): Promise<boolean> => {
      if (!firmId) {
        toast.error('Firm not found');
        return false;
      }
      try {
        switch (slotKey) {
          case 'letterhead':
            await setLetterhead({ id: firmId, letterhead: file }).unwrap();
            break;
          case 'footer':
            await setFooter({ id: firmId, firm_footer: file }).unwrap();
            break;
          case 'logo':
            await setLogo({ id: firmId, firm_logo: file }).unwrap();
            break;
          case 'watermark':
            await setWatermark({ id: firmId, firm_watermark: file }).unwrap();
            break;
          case 'header_footer_docx':
            await setHeaderFooterDocx({ id: firmId, firm_header_footer_docx: file }).unwrap();
            break;
          default:
            break;
        }
        const slotLabel =
          COLLATERAL_SLOTS.find((s) => s.key === slotKey)?.label ??
          (slotKey === 'header_footer_docx' ? 'Header / Footer DOCX' : 'Image');
        toast.success(file ? `${slotLabel} updated` : `${slotLabel} cleared`);
        return true;
      } catch (error) {
        const errorLabel =
          COLLATERAL_SLOTS.find((s) => s.key === slotKey)?.label ??
          (slotKey === 'header_footer_docx' ? 'Header / Footer DOCX' : 'Image');
        toast.error(`Failed to update ${errorLabel.toLowerCase()}`);
        captureError(error, { tags: { feature: 'firm_templates' } });
        return false;
      }
    },
    [firmId, setLetterhead, setFooter, setLogo, setWatermark, setHeaderFooterDocx],
  );

  const getRawUrl = (slot: CollateralSlot): string | null => {
    return (firm?.[slot.rawUrlField] as string | null | undefined) ?? null;
  };

  const getCleanedUrl = (slot: CollateralSlot): string | null => {
    return (firm?.[slot.cleanedUrlField] as string | null | undefined) ?? null;
  };

  return (
    <div className="space-y-6">
      {/* Template resolution — shows how each category resolves for this firm */}
      {firmId && <FirmTemplateResolutionPanel firmId={firmId} />}

      {/* V2 Templates operator table */}
      <TemplatesV2Page firmId={firmId} />

      {/* Firm files section — condensed */}
      <section className="rounded-md border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Firm Files
          </h2>
          <p className="text-xs text-zinc-500">
            Design collateral and HIPAA forms used in generated correspondence
          </p>
        </div>

        {/* Design collateral — compact rows */}
        <div className="border-b border-zinc-100 dark:border-zinc-800">
          <div className="px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
              Design Collateral
            </h3>
          </div>
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {COLLATERAL_SLOTS.map((slot) => (
              <CollateralRow
                key={slot.key}
                slot={slot}
                rawUrl={getRawUrl(slot)}
                cleanedUrl={getCleanedUrl(slot)}
                isLoading={isAnyLoading}
                onUpload={(file) => uploadCollateral(slot.key, file)}
                onClear={() => uploadCollateral(slot.key, null)}
              />
            ))}
          </div>
        </div>

        {/* Header/Footer DOCX upload */}
        <div className="border-b border-zinc-100 dark:border-zinc-800">
          <div className="px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Header / Footer Document
            </h3>
          </div>
          <HeaderFooterDocxRow
            url={firm?.firm_header_footer_docx_url ?? null}
            isLoading={isAnyLoading}
            onUpload={(file) => uploadCollateral('header_footer_docx', file)}
            onClear={() => uploadCollateral('header_footer_docx', null)}
          />
        </div>

        {/* HIPAA forms — compact rows */}
        <div>
          <div className="px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
              HIPAA Forms
            </h3>
          </div>
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
            <div className="px-4 py-2.5">
              <p className="mb-1.5 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Attorney Relationship Form
              </p>
              <FirmHipaaStaticFileManagerContent
                downloadUrl={
                  firm?.hipaa_attorney_relationship_form_url ?? null
                }
                handleUpdate={updateAttyForm}
                handleClear={() => updateAttyForm(null)}
              />
            </div>
            <div className="px-4 py-2.5">
              <p className="mb-1.5 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Reproductive Rights Attestation
              </p>
              <FirmHipaaStaticFileManagerContent
                downloadUrl={
                  firm?.hipaa_reproductive_rights_attestation_url ?? null
                }
                handleUpdate={updateReproForm}
                handleClear={() => updateReproForm(null)}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
